import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { cleanContactFields, type ContactInput } from "@/hooks/use-customer-contacts";
import type { DBCustomerContact } from "@/lib/db-types";

/**
 * Kontaktpersonene på et lead (customer_contacts.lead_id), til bookingdialogen for en
 * lead-prosess. Bundet av lead_id og liten per lead, så ingen paginering.
 */
export function useLeadContacts(leadId: string | null | undefined, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["lead-contacts", leadId],
    queryFn: async (): Promise<DBCustomerContact[]> => {
      const { data, error } = await supabase
        .from("customer_contacts")
        .select("*")
        .eq("lead_id", leadId!)
        .eq("is_active", true)
        .is("anonymized_at", null)
        .order("is_primary", { ascending: false })
        .order("full_name", { ascending: true });
      if (error) throw error;
      return (data ?? []) as DBCustomerContact[];
    },
    enabled: !!leadId && (options.enabled ?? true),
    staleTime: 60 * 1000,
  });
}

/**
 * Ny eller rettet kontakt på et lead (RPC save_lead_contact). Går via RPC fordi
 * møtebookeren ikke har tilgang til customer_contacts; RPC-en slipper den til på
 * leads den har låst. Audit skrives av DB-triggeren på customer_contacts.
 */
export function useSaveLeadContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ContactInput & { leadId: string; contactId?: string | null }) => {
      const fields = cleanContactFields(input);
      const { data, error } = await supabase.rpc("save_lead_contact", {
        p_lead_id: input.leadId,
        p_contact_id: input.contactId ?? null,
        p_full_name: fields.full_name ?? "",
        p_title: fields.title ?? null,
        p_email: fields.email ?? null,
        p_phone: fields.phone ?? null,
      });
      if (error) throw error;
      return data as unknown as DBCustomerContact;
    },
    onSuccess: async () => {
      await invalidateFor(qc, "customer_contacts", {});
    },
    onError: (err: Error) => toast.error(`Kunne ikke lagre kontaktperson: ${err.message}`),
  });
}
